import React, { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Lock, Unlock, CheckCircle2, XCircle, Clock, KeyRound, Copy, MailWarning, Upload, UserMinus, UserCheck, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import AdminPageHeader from "./AdminPageHeader";
import PendingResetLinksPanel from "./PendingResetLinksPanel";
import BulkInviteUsersDialog from "./BulkInviteUsersDialog";

const ROLES = [
  "hr",
  "manager",
  "leadership",
  "compliance",
  // Phase 10
  "read_only_leadership",
  "legal_review",
];

export default function UsersAdmin() {
  const { readOnly } = useOutletContext();
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "pending" ? "pending" : "active";
  const [tab, setTab] = useState(initialTab);
  const [rows, setRows] = useState([]);
  const [pendingRows, setPendingRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [showCreated, setShowCreated] = useState(null);
  const [search, setSearch] = useState("");
  // Phase 8 — approval / rejection state
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approveOverrides, setApproveOverrides] = useState({
    role: "manager",
    manages_file_numbers: "",
    is_admin: false,
  });
  // Phase 13.1 — Admin-initiated password reset state
  const [resetConfirm, setResetConfirm] = useState(null); // user being reset (confirm step)
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null); // {full_name, email, temporary_password, expires_in_hours}
  const [tempPwCopied, setTempPwCopied] = useState(false);

  // Phase 17 — Bulk Invite dialog
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);

  // Phase 2 (May 2026) — admin-initiated deactivate / reactivate
  const [statusConfirm, setStatusConfirm] = useState(null); // {user, action: 'deactivate'|'reactivate'}
  const [statusReason, setStatusReason] = useState("");
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  // Phase 2 (May 2026) — hard delete user
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteConflicts, setDeleteConflicts] = useState(null);

  const currentUserId = currentUser?.id;
  // `rows` is the active-users list populated by load(). On first render
  // it's `[]`, which produces activeAdminCount=0 — the Delete link is
  // suppressed until the data lands, which is the safe default.
  const activeAdminCount = (rows || []).filter(
    (u) => u.is_admin === true && u.active !== false,
  ).length;

  const load = async () => {
    try {
      const [a, p] = await Promise.all([
        api.get("/admin/users?pending=false"),
        api.get("/admin/users?pending=true"),
      ]);
      setRows(a.data);
      setPendingRows(p.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const next = tab === "pending" ? { tab: "pending" } : {};
    setSearchParams(next, { replace: true });
  }, [tab, setSearchParams]);

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return r.email?.toLowerCase().includes(q) || r.full_name?.toLowerCase().includes(q);
  });

  const startCreate = () =>
    setCreating({
      email: "",
      full_name: "",
      role: "manager",
      manages_file_numbers: [],
      is_admin: false,
    });

  const create = async () => {
    if (readOnly) return;
    try {
      const { data } = await api.post("/admin/users", creating);
      setShowCreated(data);
      setCreating(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const save = async () => {
    if (readOnly || !editing) return;
    try {
      await api.patch(`/admin/users/${editing.id}`, editing);
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const unlock = async (u) => {
    if (readOnly) return;
    try {
      await api.post(`/admin/users/${u.id}/unlock`);
      toast.success(`${u.full_name} unlocked`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  // Phase 2 (May 2026) — deactivate / reactivate
  const handleStatusChange = async () => {
    if (!statusConfirm || readOnly) return;
    const { user: u, action } = statusConfirm;
    setStatusSubmitting(true);
    try {
      await api.post(`/admin/users/${u.id}/${action}`, {
        reason: statusReason.trim() || null,
      });
      toast.success(
        action === "deactivate"
          ? `${u.full_name} deactivated — they can no longer sign in.`
          : `${u.full_name} reactivated.`,
      );
      setStatusConfirm(null);
      setStatusReason("");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setStatusSubmitting(false);
    }
  };

  // Phase 2 (May 2026) — hard delete user
  const openDeleteDialog = (u) => {
    setDeleteTarget(u);
    setDeleteConfirmEmail("");
    setDeleteConflicts(null);
  };

  const handleHardDelete = async () => {
    if (!deleteTarget || readOnly) return;
    if (deleteConfirmEmail.trim().toLowerCase() !== (deleteTarget.email || "").toLowerCase()) return;
    setDeleteSubmitting(true);
    setDeleteConflicts(null);
    try {
      const { data } = await api.delete(`/admin/users/${deleteTarget.id}`, {
        data: { confirm_email: deleteConfirmEmail.trim() },
      });
      toast.success(`User ${data.deleted_email} permanently deleted.`);
      setDeleteTarget(null);
      setDeleteConfirmEmail("");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.conflicts) {
        setDeleteConflicts(detail);
      } else {
        toast.error(formatApiErrorDetail(detail) || err.message);
      }
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // Phase 13.1 — admin-initiated password reset
  const handleResetPassword = async () => {
    if (!resetConfirm || readOnly) return;
    setResetting(true);
    try {
      const { data } = await api.post(
        `/admin/users/${resetConfirm.id}/reset-password`,
      );
      setResetResult({
        full_name: resetConfirm.full_name,
        email: resetConfirm.email,
        temporary_password: data.temporary_password,
        must_change_password: data.must_change_password,
        expires_in_hours: data.expires_in_hours,
        refresh_tokens_revoked: data.refresh_tokens_revoked,
      });
      setResetConfirm(null);
      setTempPwCopied(false);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setResetting(false);
    }
  };

  const copyTempPassword = async () => {
    if (!resetResult?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temporary_password);
      setTempPwCopied(true);
      toast.success("Temp password copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  };

  return (
    <div data-testid="admin-users-page" className="space-y-5">
      <AdminPageHeader
        eyebrow="Admin · Users"
        title="Users"
        description="Manage active users, approve pending access requests, and reset locked accounts."
        rightSlot={
          !readOnly && tab === "active" ? (
            <div className="flex gap-2">
              <Button
                onClick={() => setBulkInviteOpen(true)}
                data-testid="user-bulk-invite-button"
                variant="outline"
                className="h-10 rounded-lg text-sm font-semibold"
              >
                <Upload className="mr-1 h-4 w-4" strokeWidth={2} />
                Bulk invite
              </Button>
              <Button
                onClick={startCreate}
                data-testid="user-add-button"
                className="goodwin-primary-btn h-10 rounded-lg text-sm font-semibold text-white"
              >
                <Plus className="mr-1 h-4 w-4" strokeWidth={2} />
                New user
              </Button>
            </div>
          ) : null
        }
      />

      {/* Phase 8 — Tab strip: Active vs Pending requests */}
      <div className="flex gap-1 border-b border-[color:var(--border-subtle)]" data-testid="users-tabs">
        <button
          onClick={() => setTab("active")}
          data-testid="users-tab-active"
          className={`relative -mb-px h-9 border-b-2 px-3 text-sm transition-colors ${
            tab === "active"
              ? "border-[color:var(--page-fg)] text-[color:var(--page-fg)]"
              : "border-transparent text-[color:var(--page-fg-secondary)] hover:text-[color:var(--page-fg)]"
          }`}
          style={{ fontWeight: tab === "active" ? 500 : 400 }}
        >
          Active users
          <span className="ml-1.5 text-xs tabular-nums text-[color:var(--page-fg-muted)]">
            ({rows.length})
          </span>
        </button>
        <button
          onClick={() => setTab("pending")}
          data-testid="users-tab-pending"
          className={`relative -mb-px h-9 border-b-2 px-3 text-sm transition-colors ${
            tab === "pending"
              ? "border-[color:var(--page-fg)] text-[color:var(--page-fg)]"
              : "border-transparent text-[color:var(--page-fg-secondary)] hover:text-[color:var(--page-fg)]"
          }`}
          style={{ fontWeight: tab === "pending" ? 500 : 400 }}
        >
          Pending requests
          {pendingRows.length > 0 && (
            <span
              className="ml-1.5 inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-white tabular-nums"
              style={{ backgroundColor: "#5EAB45" }}
              data-testid="pending-count-badge"
            >
              {pendingRows.length}
            </span>
          )}
        </button>
      </div>

      {tab === "active" && (
      <>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or email"
        className="max-w-md"
        data-testid="user-search"
        aria-label="Search users"
      />

      {!readOnly && <PendingResetLinksPanel />}

      {showCreated && (
        <div
          data-testid="user-created-banner"
          className="rounded-xl p-4"
          style={{ backgroundColor: "rgba(232,203,142,0.18)", border: "1px solid #E8CB8E" }}
        >
          <p className="text-sm text-[color:var(--page-fg)]">
            <b>{showCreated.full_name}</b> created. Share the temp password securely:
          </p>
          <p className="mt-2 font-mono text-sm text-[color:var(--page-fg)]">
            email: {showCreated.email} · temp password: <b>{showCreated.temp_password}</b>
          </p>
          <Button variant="outline" size="sm" onClick={() => setShowCreated(null)} className="mt-2">
            Got it
          </Button>
        </div>
      )}

      <div className="brand-card overflow-hidden">
        <table className="w-full text-sm">
          <caption className="sr-only">All users</caption>
          <thead className="bg-[color:var(--surface-2)] text-left text-xs uppercase tracking-wider text-[color:var(--page-fg-muted)]">
            <tr>
              <th className="px-4 py-3" scope="col">Name</th>
              <th className="px-4 py-3" scope="col">Email</th>
              <th className="px-4 py-3" scope="col">Role</th>
              <th className="px-4 py-3" scope="col">Admin</th>
              <th className="px-4 py-3" scope="col">MFA</th>
              <th className="px-4 py-3" scope="col">Status</th>
              <th className="px-4 py-3" scope="col"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border-subtle)]">
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 text-[color:var(--page-fg)]">{u.full_name}</td>
                <td className="px-4 py-3 text-[color:var(--page-fg-secondary)]">{u.email}</td>
                <td className="px-4 py-3 text-[color:var(--page-fg-secondary)]">{u.role}</td>
                <td className="px-4 py-3">{u.is_admin ? "✓" : ""}</td>
                <td className="px-4 py-3">{u.mfa_enabled ? "✓" : ""}</td>
                <td className="px-4 py-3">
                  {u.locked_until ? (
                    <span
                      className="inline-flex items-center gap-1"
                      style={{ color: "var(--danger-fg)" }}
                      data-testid={`user-locked-${u.id}`}
                    >
                      <Lock className="h-3.5 w-3.5" strokeWidth={1.8} />
                      locked
                    </span>
                  ) : u.active === false ? (
                    <span style={{ color: "var(--page-fg-muted)" }}>inactive</span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1"
                      style={{ color: "var(--success-fg)" }}
                    >
                      <span
                        aria-hidden
                        className="inline-block h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: "var(--success-fg)" }}
                      />
                      active
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {u.locked_until && !readOnly && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => unlock(u)}
                      data-testid={`user-unlock-${u.id}`}
                      className="mr-2"
                    >
                      <Unlock className="mr-1 h-3.5 w-3.5" />
                      Unlock
                    </Button>
                  )}
                  {!readOnly && u.active !== false && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setResetConfirm(u)}
                      data-testid={`user-reset-password-${u.id}`}
                      className="mr-2"
                      title="Reset password — generates a one-time temp password"
                    >
                      <KeyRound className="mr-1 h-3.5 w-3.5" />
                      Reset password
                    </Button>
                  )}
                  {!readOnly && u.active !== false && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setStatusConfirm({ user: u, action: "deactivate" }); setStatusReason(""); }}
                      data-testid={`user-deactivate-${u.id}`}
                      className="mr-2"
                      title="Deactivate — blocks sign-in. Audit trail preserved. Reversible."
                    >
                      <UserMinus className="mr-1 h-3.5 w-3.5" />
                      Deactivate
                    </Button>
                  )}
                  {!readOnly && u.active === false && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setStatusConfirm({ user: u, action: "reactivate" }); setStatusReason(""); }}
                      data-testid={`user-reactivate-${u.id}`}
                      className="mr-2"
                      title="Reactivate — restore sign-in for this user."
                    >
                      <UserCheck className="mr-1 h-3.5 w-3.5" />
                      Reactivate
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing({ ...u })}
                    disabled={readOnly}
                    data-testid={`user-edit-${u.id}`}
                  >
                    Edit
                  </Button>
                  {/* Phase 2 — hard delete. Visually de-emphasised (text link) so it's not a single-click hazard. */}
                  {!readOnly
                    && u.id !== currentUserId
                    && !(u.is_admin === true && u.active !== false && activeAdminCount <= 1) && (
                    <button
                      type="button"
                      onClick={() => openDeleteDialog(u)}
                      data-testid={`user-delete-${u.id}`}
                      className="ml-2 text-xs text-[color:var(--page-fg-muted)] underline-offset-2 hover:text-[color:var(--danger-fg)] hover:underline"
                      title="Permanently delete this user. Cannot be undone."
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}

      {tab === "pending" && (
        <div className="brand-card overflow-hidden" data-testid="pending-users-table">
          {pendingRows.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-[color:var(--page-fg-secondary)]" data-testid="pending-empty">
              No pending access requests right now.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--surface-2)] text-left text-xs uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                <tr>
                  <th className="px-4 py-3" scope="col">Name</th>
                  <th className="px-4 py-3" scope="col">Email</th>
                  <th className="px-4 py-3" scope="col">Requested role</th>
                  <th className="px-4 py-3" scope="col">Reason</th>
                  <th className="px-4 py-3" scope="col">Submitted</th>
                  <th className="px-4 py-3" scope="col"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--border-subtle)]">
                {pendingRows.map((u) => (
                  <tr key={u.id} data-testid={`pending-row-${u.id}`}>
                    <td className="px-4 py-3 text-[color:var(--page-fg)]">{u.full_name}</td>
                    <td className="px-4 py-3 text-[color:var(--page-fg-secondary)]">{u.email}</td>
                    <td className="px-4 py-3 text-[color:var(--page-fg-secondary)]">{u.requested_role}</td>
                    <td className="px-4 py-3 text-xs text-[color:var(--page-fg-secondary)]" style={{ maxWidth: 280 }}>
                      <span className="line-clamp-2" title={u.request_reason || ""}>
                        {u.request_reason || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[color:var(--page-fg-secondary)]">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {(u.requested_at || "").slice(0, 10)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!readOnly && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => {
                              setApproving(u);
                              setApproveOverrides({
                                role: u.requested_role || "manager",
                                manages_file_numbers: "",
                                is_admin: false,
                              });
                            }}
                            data-testid={`pending-approve-${u.id}`}
                            className="mr-2 text-white"
                            style={{ backgroundColor: "#5EAB45" }}
                          >
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setRejecting(u);
                              setRejectReason("");
                            }}
                            data-testid={`pending-reject-${u.id}`}
                          >
                            <XCircle
                              className="mr-1 h-3.5 w-3.5"
                              strokeWidth={1.8}
                              style={{ color: "var(--danger-fg)" }}
                            />
                            Reject
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Approval modal */}
      <Dialog open={!!approving} onOpenChange={(v) => !v && setApproving(null)}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve access request</DialogTitle>
            <DialogDescription>
              {approving?.full_name} — {approving?.email}. The user will be
              notified by email and able to sign in immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Role</label>
              <Select
                value={approveOverrides.role}
                onValueChange={(v) => setApproveOverrides((o) => ({ ...o, role: v }))}
              >
                <SelectTrigger data-testid="approve-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {approveOverrides.role === "manager" && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                  Manages position IDs (comma-separated)
                </label>
                <Input
                  value={approveOverrides.manages_file_numbers}
                  onChange={(e) =>
                    setApproveOverrides((o) => ({ ...o, manages_file_numbers: e.target.value }))
                  }
                  data-testid="approve-files"
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch
                checked={!!approveOverrides.is_admin}
                onCheckedChange={(v) => setApproveOverrides((o) => ({ ...o, is_admin: v }))}
                data-testid="approve-is-admin"
              />
              <span className="text-sm">HR Admin (only valid when role=hr)</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproving(null)}>
              Cancel
            </Button>
            <Button
              data-testid="approve-confirm"
              className="text-white"
              style={{ backgroundColor: "#5EAB45" }}
              onClick={async () => {
                try {
                  const body = { role: approveOverrides.role, is_admin: approveOverrides.is_admin };
                  if (approveOverrides.role === "manager" && approveOverrides.manages_file_numbers.trim()) {
                    body.manages_file_numbers = approveOverrides.manages_file_numbers
                      .split(",")
                      .map((v) => parseInt(v.trim(), 10))
                      .filter((n) => !Number.isNaN(n));
                  }
                  await api.post(`/admin/users/${approving.id}/approve`, body);
                  toast.success(`${approving.full_name} approved`);
                  setApproving(null);
                  load();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Approval failed");
                }
              }}
            >
              Approve & notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection modal */}
      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject access request</DialogTitle>
            <DialogDescription>
              {rejecting?.full_name} — {rejecting?.email}. They will receive an
              email with the reason you provide below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Reason (sent to requester)</label>
            <Textarea
              rows={3}
              maxLength={500}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Role does not align with current organisation needs."
              data-testid="reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim()}
              data-testid="reject-confirm"
              onClick={async () => {
                try {
                  await api.post(`/admin/users/${rejecting.id}/reject`, { reason: rejectReason.trim() });
                  toast.success(`${rejecting.full_name} rejected`);
                  setRejecting(null);
                  load();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reject failed");
                }
              }}
            >
              Reject & notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {creating && !readOnly && (
        <div className="brand-card p-5">
          <h2 className="text-base font-semibold text-[color:var(--page-fg)]">New user</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Input
              value={creating.email}
              onChange={(e) => setCreating({ ...creating, email: e.target.value })}
              placeholder="email@goodwinliving.test"
              aria-label="Email"
              data-testid="user-create-email"
            />
            <Input
              value={creating.full_name}
              onChange={(e) => setCreating({ ...creating, full_name: e.target.value })}
              placeholder="Full name"
              aria-label="Full name"
              data-testid="user-create-name"
            />
            <Select value={creating.role} onValueChange={(v) => setCreating({ ...creating, role: v })}>
              <SelectTrigger data-testid="user-create-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={creating.manages_file_numbers.join(",")}
              onChange={(e) =>
                setCreating({
                  ...creating,
                  manages_file_numbers: e.target.value
                    .split(",")
                    .map((v) => parseInt(v.trim(), 10))
                    .filter((n) => !Number.isNaN(n)),
                })
              }
              placeholder="Manages position IDs (comma-separated)"
              aria-label="Manages position IDs"
              data-testid="user-create-files"
            />
            <div className="flex items-center gap-2">
              <Switch
                checked={!!creating.is_admin}
                onCheckedChange={(v) => setCreating({ ...creating, is_admin: v })}
                aria-label="Is admin"
                data-testid="user-create-is-admin"
              />
              <span className="text-sm">HR Admin (only valid when role=hr)</span>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={create}
              data-testid="user-create-submit"
              className="goodwin-primary-btn text-white"
            >
              Create + show temp password
            </Button>
            <Button variant="outline" onClick={() => setCreating(null)}>Cancel</Button>
          </div>
        </div>
      )}

      {editing && !readOnly && (
        <div className="brand-card p-5">
          <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Edit user</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Input
              value={editing.full_name || ""}
              onChange={(e) => setEditing({ ...editing, full_name: e.target.value })}
              aria-label="Full name"
              data-testid="user-edit-name"
            />
            <Input
              value={editing.email || ""}
              onChange={(e) => setEditing({ ...editing, email: e.target.value })}
              aria-label="Email"
              data-testid="user-edit-email"
            />
            <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
              <SelectTrigger data-testid="user-edit-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Switch
                checked={editing.active !== false}
                onCheckedChange={(v) => setEditing({ ...editing, active: v })}
                aria-label="Active"
                data-testid="user-edit-active"
              />
              <span className="text-sm">Active</span>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={!!editing.is_admin}
                onCheckedChange={(v) => setEditing({ ...editing, is_admin: v })}
                aria-label="Admin"
                data-testid="user-edit-is-admin"
              />
              <span className="text-sm">HR Admin</span>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={!!editing.force_password_reset}
                onCheckedChange={(v) => setEditing({ ...editing, force_password_reset: v })}
                aria-label="Force password reset"
                data-testid="user-edit-force-reset"
              />
              <span className="text-sm">Force password reset on next login</span>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={save} data-testid="user-edit-save" className="goodwin-primary-btn text-white">
              Save
            </Button>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Phase 2 (May 2026) — Hard-delete user confirm dialog */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!deleteSubmitting && !open) {
            setDeleteTarget(null);
            setDeleteConfirmEmail("");
            setDeleteConflicts(null);
          }
        }}
      >
        <DialogContent data-testid="user-delete-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-[color:var(--danger-fg)]" />
              Permanently delete this user?
            </DialogTitle>
            <DialogDescription>
              <span className="font-semibold">{deleteTarget?.full_name}</span>{" "}
              ({deleteTarget?.email}) will be removed forever.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div
              className="rounded-md border p-3 text-[12.5px] leading-relaxed"
              style={{
                backgroundColor: "var(--danger-bg)",
                borderColor: "var(--danger-border)",
                color: "var(--page-fg)",
              }}
            >
              This permanently removes the user account. Their past actions in
              audit logs remain immutable but will display as a deleted user.
              <strong> This cannot be undone.</strong> If you might need this
              account again, use <em>Deactivate</em> instead.
            </div>
            {deleteConflicts && (
              <div
                className="rounded-md border p-3 text-[12.5px] leading-relaxed"
                style={{
                  backgroundColor: "var(--warning-bg)",
                  borderColor: "var(--warning-border)",
                  color: "var(--page-fg)",
                }}
                data-testid="user-delete-conflicts"
              >
                <p className="font-semibold mb-1">Reassign these first:</p>
                <ul className="ml-4 list-disc">
                  {deleteConflicts.conflicts?.cases > 0 && (
                    <li>{deleteConflicts.conflicts.cases} active case(s) assigned to this user</li>
                  )}
                  {deleteConflicts.conflicts?.team_members > 0 && (
                    <li>{deleteConflicts.conflicts.team_members} team member(s) where they're the HR partner or manager</li>
                  )}
                  {Array.isArray(deleteConflicts.conflicts?.service_lines)
                    && deleteConflicts.conflicts.service_lines.length > 0 && (
                    <li>Default HR partner on service line(s): <span className="font-mono">{deleteConflicts.conflicts.service_lines.join(", ")}</span></li>
                  )}
                  {deleteConflicts.conflicts?.last_active_admin && (
                    <li>This is the last active admin — promote someone else first</li>
                  )}
                </ul>
              </div>
            )}
            <div>
              <Label
                className="text-xs uppercase tracking-wider"
                style={{ color: "var(--page-fg-muted)" }}
              >
                Type the user's email to confirm:{" "}
                <span className="font-mono" style={{ color: "var(--page-fg)" }}>
                  {deleteTarget?.email}
                </span>
              </Label>
              <Input
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                data-testid="user-delete-confirm-email"
                placeholder={deleteTarget?.email}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>
          <DialogFooter className="mt-3 gap-2">
            <Button
              variant="outline"
              onClick={() => { setDeleteTarget(null); setDeleteConfirmEmail(""); setDeleteConflicts(null); }}
              disabled={deleteSubmitting}
              data-testid="user-delete-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleHardDelete}
              disabled={
                deleteSubmitting
                || deleteConfirmEmail.trim().toLowerCase() !== (deleteTarget?.email || "").toLowerCase()
              }
              data-testid="user-delete-confirm"
              className="bg-[color:var(--danger-fg)] hover:bg-[color:var(--danger-fg)] hover:opacity-90 text-white"
            >
              {deleteSubmitting ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 2 (May 2026) — Confirm deactivate / reactivate dialog */}
      <Dialog
        open={!!statusConfirm}
        onOpenChange={(open) => {
          if (!statusSubmitting && !open) {
            setStatusConfirm(null);
            setStatusReason("");
          }
        }}
      >
        <DialogContent data-testid="user-status-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {statusConfirm?.action === "deactivate"
                ? "Deactivate this user?"
                : "Reactivate this user?"}
            </DialogTitle>
            <DialogDescription>
              {statusConfirm?.action === "deactivate" ? (
                <>
                  <span className="font-semibold">
                    {statusConfirm?.user?.full_name} ({statusConfirm?.user?.email})
                  </span>{" "}
                  will be unable to sign in. Their existing sessions will be
                  signed out immediately. The audit trail and all historical
                  case ownership remain intact. This action is reversible.
                </>
              ) : (
                <>
                  <span className="font-semibold">
                    {statusConfirm?.user?.full_name} ({statusConfirm?.user?.email})
                  </span>{" "}
                  will be able to sign in again. Failed-login counters and any
                  lock from the inactive window are cleared.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-1">
            <label className="text-xs uppercase tracking-wider text-[color:var(--page-fg-muted)]">
              Reason (optional, recorded in the audit log)
            </label>
            <Textarea
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              rows={3}
              maxLength={500}
              data-testid="user-status-reason"
              placeholder={
                statusConfirm?.action === "deactivate"
                  ? "e.g. Left the organization, role no longer requires access"
                  : "e.g. Returning from leave, role re-assigned"
              }
            />
          </div>
          <DialogFooter className="mt-3 gap-2">
            <Button
              variant="outline"
              onClick={() => { setStatusConfirm(null); setStatusReason(""); }}
              disabled={statusSubmitting}
              data-testid="user-status-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleStatusChange}
              disabled={statusSubmitting}
              data-testid="user-status-confirm"
            >
              {statusSubmitting
                ? "Working…"
                : statusConfirm?.action === "deactivate"
                  ? "Deactivate"
                  : "Reactivate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 13.1 — Confirm-reset dialog */}
      <Dialog
        open={!!resetConfirm}
        onOpenChange={(open) => {
          if (!resetting && !open) setResetConfirm(null);
        }}
      >
        <DialogContent data-testid="reset-password-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Reset password?</DialogTitle>
            <DialogDescription>
              A new one-time temp password will be generated for{" "}
              <span className="font-semibold">
                {resetConfirm?.full_name} ({resetConfirm?.email})
              </span>
              . Their existing sessions will be signed out immediately and they
              will be required to set a new password on next login.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 gap-2">
            <Button
              variant="outline"
              onClick={() => setResetConfirm(null)}
              disabled={resetting}
              data-testid="reset-password-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetting}
              className="goodwin-primary-btn text-white"
              data-testid="reset-password-confirm"
            >
              <KeyRound className="mr-1 h-4 w-4" />
              {resetting ? "Resetting…" : "Generate temp password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 13.1 — One-time temp password reveal dialog */}
      <Dialog
        open={!!resetResult}
        onOpenChange={(open) => {
          if (!open) setResetResult(null);
        }}
      >
        <DialogContent data-testid="reset-password-result-dialog">
          <DialogHeader>
            <DialogTitle>
              <span className="inline-flex items-center gap-2">
                <KeyRound className="h-4 w-4" style={{ color: "var(--warning-fg)" }} />
                Temp password ready
              </span>
            </DialogTitle>
            <DialogDescription>
              For <span className="font-semibold">{resetResult?.full_name}</span> ·{" "}
              {resetResult?.email}
            </DialogDescription>
          </DialogHeader>
          <div
            className="mt-2 rounded-lg p-3"
            style={{
              backgroundColor: "var(--surface-2)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            <p
              className="text-[10px] font-semibold uppercase tracking-[0.10em]"
              style={{ color: "var(--page-fg-muted)" }}
            >
              Temporary password (one-time)
            </p>
            <code
              data-testid="reset-password-temp-value"
              className="mt-1 block break-all font-mono text-base"
              style={{ color: "var(--page-fg)" }}
            >
              {resetResult?.temporary_password}
            </code>
          </div>
          <div
            className="mt-3 rounded-md px-3 py-2 text-xs"
            style={{
              backgroundColor: "var(--warning-bg)",
              color: "var(--warning-fg)",
              border: "1px solid var(--warning-border)",
            }}
            data-testid="reset-password-warning"
          >
            <strong>Heads up:</strong> this is the only time this password is
            shown. It is not stored in readable form anywhere. Hand it to{" "}
            {resetResult?.full_name?.split(" ")[0] || "the user"} securely.
            They will be forced to change it on first sign-in.
          </div>
          <DialogFooter className="mt-3 flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={copyTempPassword}
              data-testid="reset-password-copy-temp"
              className="w-full sm:w-auto"
            >
              <Copy className="mr-1 h-4 w-4" />
              {tempPwCopied ? "Copied" : "Copy password"}
            </Button>
            <Button
              onClick={() => setResetResult(null)}
              data-testid="reset-password-close"
              className="goodwin-primary-btn w-full text-white sm:w-auto"
            >
              I've handed it off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Phase 17 — Bulk Invite */}
      <BulkInviteUsersDialog
        open={bulkInviteOpen}
        onOpenChange={setBulkInviteOpen}
        onComplete={() => {
          load();
        }}
      />
    </div>
  );
}
